import { useEffect, useMemo, useState } from 'react';
import { Check, Minus, Plus, ShoppingCart, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { GiftCardTile } from './GiftCardTile';
import { useGiftCardCart } from '../context/GiftCardCartContext';
import { GIFT_CARD_MAX_QUANTITY } from '../lib/rewardCatalogue';
import { getRewardDenominations, getRewardRange } from '../lib/rewardDiscount';
import { useFaceQuote } from '../lib/giftCardQuote';
import { formatMoney } from '../lib/money';

/**
 * Quick add, from the catalogue.
 *
 * A sheet rather than a panel drawn inside the card. The first attempt at this
 * lived on the tile itself, which is the smallest surface on the page — nine
 * denominations had to scroll inside it, and it covered the artwork the customer
 * was using to recognise the brand. Amazon and Flipkart both take this to an
 * overlay, and a gift card needs more room than they do because there are three
 * numbers per option rather than one.
 *
 * Bottom sheet on a phone, centred dialog from `sm` up.
 *
 * The product page is still where terms, redemption instructions and expiry
 * live. Quick add never navigates there — that is the whole point of it.
 */
export function GiftCardQuickAdd({ reward, open, onClose }) {
  const { addGiftCard } = useGiftCardCart();

  /*
    Memoised, because both are rebuilt on every render otherwise and both end up
    in dependency arrays. An unstable `range` re-ran the reset below on every
    render, which put the minimum back in the field between keystrokes, and
    restarted the pricing effect before it could finish.
  */
  const denominations = useMemo(() => getRewardDenominations(reward), [reward]);
  const range = useMemo(() => getRewardRange(reward), [reward]);
  const isRange = !!range && !denominations.length;

  const [selected, setSelected] = useState(null);
  const [face, setFace] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);

  // A fresh sheet every time. Left alone, a second card would open showing the
  // amount picked on the first.
  const minFace = range?.minFace;
  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setQuantity(1);
    setFace(isRange && minFace != null ? minFace : '');
  }, [open, reward?.id, isRange, minFace]);

  // Range cards are priced by the server as the customer types.
  const { row: quoted, pricing } = useFaceQuote({
    rewardId: reward?.id,
    range: isRange ? range : null,
    face,
    quantity: 1,
  });

  if (!open) return null;

  /*
    One shape, whichever route got here — `payable`, `face`, `savings`.

    The two sources name the payable amount differently: a quote row calls it
    `payable_amount`, and getRewardDenominations calls it `price`. This used to
    spread the option and read `chosen.value`, which is the name only the product
    page's own mapping uses — so on a fixed card the payable was undefined, "You
    pay" rendered "—" beside a filled-in value and saving, and Add to Cart threw
    "This gift card has no amount selected". Normalising once here is what stops
    that being possible.
  */
  const chosen = isRange
    ? (quoted && {
      payable: Number(quoted.payable_amount),
      face: Number(quoted.face_value),
      savings: Number(quoted.discount_amount) || 0,
      maxQuantity: quoted.max_quantity ?? GIFT_CARD_MAX_QUANTITY,
    })
    : (selected && {
      payable: selected.price,
      face: selected.face,
      savings: selected.savings || 0,
      maxQuantity: GIFT_CARD_MAX_QUANTITY,
    });

  const maxQty = chosen?.maxQuantity ?? GIFT_CARD_MAX_QUANTITY;
  const outOfRange = isRange && face !== '' && (Number(face) < range.minFace || Number(face) > range.maxFace);

  /**
   * Add, close, stay put.
   *
   * No redirect to the cart or the product page — the whole point of quick add
   * is that the customer keeps browsing, and a basket for a team is built by
   * doing this several times without leaving the grid. The header badge updates
   * from the same context the cart reads, so it moves the moment this returns.
   */
  const submit = () => {
    if (!chosen) return;
    setAdding(true);
    try {
      const { added, capped } = addGiftCard(reward, chosen.payable, quantity);

      if (added === 0) {
        toast.error(`Your cart already holds the maximum ${maxQty} of this card at this value.`);
        return;
      }

      // A short confirmation for the normal case; the cap is a different event
      // and has to say what it did, because fewer were added than were asked for.
      if (capped) {
        toast.warning(`Added ${added} — ${maxQty} is the most of one card at one value per order.`);
      } else {
        toast.success('Added to cart');
      }
      onClose();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9998] flex items-end justify-center sm:items-center">
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add gift card to cart"
        className="relative flex max-h-[88vh] w-full flex-col rounded-t-3xl bg-white shadow-2xl sm:max-w-md sm:rounded-3xl"
        data-testid="quick-add-sheet"
      >
        <div className="flex items-start gap-3 border-b border-slate-100 p-4">
          <div className="w-20 flex-shrink-0">
            <GiftCardTile reward={reward} size="sm" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="line-clamp-2 font-body font-semibold text-secondary">
              {reward?.brand?.name || reward?.display_parameters?.name}
            </h2>
            <p className="mt-0.5 font-body text-[12px] text-muted-foreground">
              {isRange ? 'Choose any value' : 'Choose a value'}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="-mr-1 -mt-1 rounded-full p-2 text-muted-foreground transition-colors hover:bg-surface hover:text-secondary"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {isRange ? (
            <div className="space-y-3">
              <label className="block font-body text-sm font-medium text-secondary" htmlFor="quick-add-face">
                Gift card value ({formatMoney(range.minFace)} – {formatMoney(range.maxFace)})
              </label>
              <div className="flex items-center gap-2">
                <span className="font-body text-lg font-semibold text-secondary">₹</span>
                <input
                  id="quick-add-face"
                  type="number"
                  min={range.minFace}
                  max={range.maxFace}
                  value={face}
                  onChange={(e) => setFace(e.target.value === '' ? '' : Number(e.target.value))}
                  className="w-40 rounded-xl border-2 border-gray-100 px-4 py-2 font-body text-sm outline-none focus:border-primary"
                  data-testid="quick-add-amount"
                />
              </div>
              {outOfRange && (
                <p className="font-body text-xs text-destructive">
                  Value must be between {formatMoney(range.minFace)} and {formatMoney(range.maxFace)}.
                </p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {denominations.map((d) => {
                const active = selected?.pricePaise === d.pricePaise;
                return (
                  <button
                    key={d.pricePaise}
                    onClick={() => setSelected(d)}
                    className={`relative rounded-xl border-2 px-2 py-3 text-center transition-all ${active
                      ? 'border-primary bg-primary/[0.06]'
                      : 'border-slate-200 hover:border-slate-300'}`}
                    data-testid={`quick-add-denomination-${d.pricePaise}`}
                  >
                    {active && (
                      <span className="absolute right-1 top-1 grid h-4 w-4 place-items-center rounded-full bg-primary text-white">
                        <Check className="h-2.5 w-2.5" strokeWidth={3} />
                      </span>
                    )}
                    <p className="font-body text-[15px] font-bold leading-none text-secondary">
                      {formatMoney(d.face ?? d.price)}
                    </p>
                    <p className="mt-1 font-body text-[10px] text-muted-foreground">
                      Pay {formatMoney(d.price)}
                    </p>
                  </button>
                );
              })}
            </div>
          )}

          {/* One summary, same shape whether or not anything is chosen yet, so
              the sheet does not jump as the customer picks. */}
          <dl className="mt-4 space-y-2.5 rounded-2xl bg-surface p-4 font-body text-sm">
            <div className="flex items-baseline justify-between">
              <dt className="text-muted-foreground">Gift card value</dt>
              <dd className="font-semibold text-secondary">
                {formatMoney(chosen ? (chosen.face ?? chosen.payable) * quantity : null)}
              </dd>
            </div>
            <div className="flex items-baseline justify-between">
              <dt className="text-muted-foreground">You save</dt>
              <dd className={`font-semibold ${chosen?.savings > 0 ? 'text-success' : 'text-secondary'}`}>
                {formatMoney(chosen ? (chosen.savings || 0) * quantity : null)}
              </dd>
            </div>
            <div className="flex items-baseline justify-between border-t border-slate-200 pt-2.5">
              <dt className="font-semibold text-secondary">You pay</dt>
              <dd className="font-body text-xl font-bold text-secondary" data-testid="quick-add-payable">
                {pricing ? '—' : formatMoney(chosen ? chosen.payable * quantity : null)}
              </dd>
            </div>
          </dl>
        </div>

        <div className="flex items-center gap-3 border-t border-slate-100 p-4">
          <div className="flex flex-shrink-0 items-center rounded-full border border-gray-200">
            <button
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              className="rounded-l-full p-2.5 transition-colors hover:bg-surface"
              aria-label="Decrease quantity"
            >
              <Minus className="h-4 w-4" />
            </button>
            <span className="w-9 text-center font-body text-sm">{quantity}</span>
            <button
              onClick={() => setQuantity((q) => Math.min(maxQty, q + 1))}
              disabled={quantity >= maxQty}
              title={quantity >= maxQty
                ? `${maxQty} is the most of one card at one value that can be issued in a single order`
                : undefined}
              className="rounded-r-full p-2.5 transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Increase quantity"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>

          <Button
            onClick={submit}
            disabled={!chosen || adding || pricing}
            className="btn-press flex-1 rounded-full bg-primary py-6 font-body font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
            data-testid="quick-add-submit"
          >
            {/* The price is not in the button. It is stated once, in the summary
                directly above — repeating it here made the same number appear
                twice a few pixels apart, and a figure on a button reads as
                something the button will change rather than something it
                confirms. */}
            <ShoppingCart className="mr-2 h-4 w-4" />
            Add to Cart
          </Button>
        </div>
      </div>
    </div>
  );
}

export default GiftCardQuickAdd;
